import * as React from 'react';
import Button from '@mui/material/Button';
import { alpha, styled } from '@mui/material/styles';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Alert from '@mui/material/Alert';
import Chip from '@mui/material/Chip';
import { useLanguage } from '../context/LanguageContext';
import { Box, Stack, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import constants from '../constants';
import Iconify from '../components/Iconify';
import { useAuth } from '../context/AuthContext';
import { getUsageAccessInfo } from '../services/helper';

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    width: '100%',
    maxWidth: 560,
    borderRadius: 24,
    overflow: 'hidden',
  },
  '& .MuiDialogContent-root': {
    padding: 0,
  },
  '& .MuiDialogActions-root': {
    padding: theme.spacing(2.5, 3, 3),
  },
}));

export default function AlertForPremium({ open, onClose, error }) {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const accessInfo = getUsageAccessInfo(currentUser);
  const labelGoPremium = t('labelGoPremium'); // Go Premium
  const labelCancel = t('cancel'); // cancel
  const labelJoinPremium = t('joinPremium'); // Join Premium
  const labelDiscount = t('discount'); // discount
  const benefits = [
    { icon: 'solar:microphone-3-bold-duotone', title: 'Unlimited voice typing', text: 'Keep dictating after free days or trial uses end.' },
    { icon: 'solar:command-bold-duotone', title: 'Custom commands', text: 'Create shortcuts, snippets, and repeated phrases.' },
    { icon: 'solar:magic-stick-3-bold-duotone', title: 'AI command support', text: 'Use computer-style commands for assisted writing.' },
  ];

  const handleClose = () => {
    onClose();
  };
  const makePayment = () => {
    navigate('/dashboard/payment');
    onClose();
  };

  return (
    <BootstrapDialog aria-labelledby="join-premium-dialog" open={open} onClose={handleClose}>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ borderRadius: 0 }}>
            <b>{error}</b>
          </Alert>
        )}
        <Box
          sx={{
            p: { xs: 3, md: 4 },
            color: 'common.white',
            background: 'linear-gradient(135deg, #171717 0%, #23221F 58%, #0F3D28 100%)',
          }}
        >
          <Stack spacing={2.5}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
              <Chip
                label={`90% ${labelDiscount}`}
                color="secondary"
                sx={{ fontWeight: 800 }}
              />
              {!accessInfo.paid && (
                <Typography variant="caption" sx={{ color: alpha('#FFFFFF', 0.72) }}>
                  {accessInfo.trialsLeft} trials · {accessInfo.daysLeft} days left
                </Typography>
              )}
            </Stack>
            <Box>
              <Typography id="join-premium-dialog" variant="h3" sx={{ color: 'common.white', lineHeight: 1.12 }}>
                {labelJoinPremium}
              </Typography>
              <Typography sx={{ mt: 1.5, color: alpha('#FFFFFF', 0.72), lineHeight: 1.8 }}>
                Upgrade once and keep the voice workspace open for unlimited browser dictation, custom commands, and faster writing.
              </Typography>
            </Box>
            <Box sx={{ p: 2, borderRadius: 2, bgcolor: alpha('#FFFFFF', 0.08), border: `1px solid ${alpha('#FFFFFF', 0.12)}` }}>
              <Typography variant="overline" sx={{ color: alpha('#FFFFFF', 0.62) }}>
                Lifetime access
              </Typography>
              <Typography variant="h4" sx={{ color: 'common.white' }}>
                {constants.premium.pricing.mini}
              </Typography>
            </Box>
          </Stack>
        </Box>

        <Stack spacing={1.5} sx={{ p: { xs: 2.5, md: 3 }, bgcolor: 'background.paper' }}>
          {benefits.map((benefit) => (
            <Stack
              key={benefit.title}
              direction="row"
              spacing={1.5}
              sx={{ p: 1.5, borderRadius: 2, bgcolor: 'background.default' }}
            >
              <Box
                sx={{
                  width: 42,
                  height: 42,
                  borderRadius: 2,
                  bgcolor: 'secondary.lighter',
                  color: 'secondary.dark',
                  display: 'grid',
                  placeItems: 'center',
                  flexShrink: 0,
                }}
              >
                <Iconify icon={benefit.icon} width={22} height={22} />
              </Box>
              <Box>
                <Typography variant="subtitle2">{benefit.title}</Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  {benefit.text}
                </Typography>
              </Box>
            </Stack>
          ))}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} color="inherit">
          {labelCancel}
        </Button>
        <Button autoFocus size="large" variant="contained" onClick={makePayment}>
          {labelGoPremium}
        </Button>
      </DialogActions>
    </BootstrapDialog>
  );
}
